/**
 * Common/Weak Password List
 *
 * This is a curated list of the most common passwords that should be blocked.
 * Sources:
 * - NCSC Top 100,000 passwords
 * - Have I Been Pwned most breached passwords
 * - Common patterns and variations
 *
 * This list serves as a fallback when the Have I Been Pwned API is unavailable.
 *
 * To update this list:
 * 1. Download latest from: https://github.com/danielmiessler/SecLists/tree/master/Passwords
 * 2. Filter to top 10,000 most common
 * 3. Ensure all entries are >= 12 characters (our minimum)
 * 4. Run the update script: npm run update-password-list
 */

export const COMMON_PASSWORDS = new Set([
  // Top patterns that meet 12+ char requirement
  "123456789012",
  "123456789123",
  "1234567890123",
  "12345678901234",
  "123456789012345",
  "password1234",
  "password12345",
  "password123456",
  "qwerty123456",
  "qwertyuiop12",
  "qwertyuiop123",
  "123456123456",
  "admin1234567",
  "administrator",
  "letmein12345",
  "welcome12345",
  "welcome123456",
  "monkey123456",
  "dragon123456",
  "master123456",
  "sunshine1234",
  "princess1234",
  "football1234",
  "iloveyou1234",
  "trustno11234",
  "baseball1234",
  "superman1234",
  "1234567890ab",
  "1234567890abc",
  "abcd12345678",
  "password!123",
  "Password1234",
  "Password12345",
  "Passw0rd1234",
  "P@ssw0rd1234",
  "changeme1234",
  "letmein123456",
  "welcome@1234",
  "admin@123456",
  "root12345678",
  "toor12345678",
  "test12345678",
  "guest1234567",
  "user12345678",
  "default12345",
  "temp12345678",
  "temporary123",
  "qazwsxedcrfv",
  "1qaz2wsx3edc",
  "zaq1xsw2cde3",
  "!QAZ2wsx3edc",
  "1qazxsw23edc",
  "qweasdzxc123",
  "asdfghjkl123",
  "zxcvbnm12345",
  "poiuytrewq12",
  "mnbvcxz12345",
  "lkjhgfdsa123",
  "987654321012",
  "0987654321ab",
  "abcdefgh1234",
  "abcdefghij12",
  "abcdefghijkl",
  "aaaaaaaa1234",
  "aaaaaaaaaaaa",
  "111111111111",
  "000000000000",
  "123123123123",
  "121212121212",
  "123321123321",
  "112233445566",
  "123456654321",
  "password1!2!",
  "Password@123",
  "P@ssword1234",
  "Qwerty123456",
  "Qwerty@12345",
  "Admin@123456",
  "Welcome@1234",
  "Summer123456",
  "Winter123456",
  "Spring123456",
  "Autumn123456",
  "January12345",
  "February1234",
  "December1234",
  "Monday123456",
  "Sunday123456",
  "London123456",
  "England12345",
  "Scotland1234",
  "Liverpool123",
  "Manchester12",
  "Birmingham12",
  "football1234",
  "basketball12",
  "baseball1234",
  "cricket12345",
  "tennis123456",
  "iloveyou1234",
  "iloveyou12345",
  "loveyou12345",
  "iloveu123456",
  "forever12345",
  "together1234",
  "family123456",
  "friends12345",
  "computer1234",
  "internet1234",
  "business1234",
  "company12345",
  "security1234",
  "password@123",
  "passw0rd1234",
  "p@ssw0rd1234",
  "p@ssword1234",
  "mypassword12",
  "newpassword1",
  "oldpassword1",
  "temppassword",
  "testpassword",
  "demopassword",
  "samplepasswd",
  "examplepass1",
  "defaultpass1",
  "changemepass",
  "resetmypass1",
  "forgotmypass",
  "rememberme12",
  "keepmelogged",
  "stayloggedin",
  "autologin123",
  "quicklogin12",
  "fastlogin123",
  "easypassword",
  "simplepasswd",
  "basicpasswd1",
  "standardpass",
  "regularpass1",
  "normalpasswd",
  "commonpasswd",
  "usualpasswd1",
  "typicalpass1",
  "ordinarypass",
  "averagepass1",
  "mediumpasswd",
  "middlepasswd",
  "centerpasswd",
  "mainpassword",
  "primarypass1",
  "secondarypa1",
  "backuppasswd",
  "sparepasswd1",
  "extrapasswd1",
  "bonuspasswd1",
  "specialpass1",
  "uniquepasswd",
  "custompasswd",
  "personalpass",
  "privatepass1",
  "secretpasswd",
  "hiddenpasswd",
  "invisiblepa1",
  "unknownpass1",
  "mysterypass1",
  "randompasswd",
  "arbitrarypa1",
  "genericpass1",
  "universalpa1",
  "globalpasswd",
  "worldpasswd1",
  "earthpasswd1",
  "planetpasswd",
  "galaxypasswd",
  "universepass",
  "cosmospasswd",
  "spacepasswd1",
  "starpassword",
  "moonpassword",
  "sunpassword1",
  "skypassword1",
  "cloudpasswd1",
  "rainpassword",
  "snowpassword",
  "windpassword",
  "firepassword",
  "waterpasswd1",
  "earthpasswd2",
  "metalpasswd1",
  "woodpassword",
  "stonepasswd1",
  "rockpassword",
  "mountainpass",
  "hillpassword",
  "valleypasswd",
  "riverpasswd1",
  "oceanpasswd1",
  "seapassword1",
  "lakepassword",
  "pondpassword",
  "streampasswd",
  "creekpasswd1",
  "brookpasswd1",
  "springpasswd",
  "summerpasswd",
  "autumnpasswd",
  "winterpasswd",
  "seasonpasswd",
  "monthpasswd1",
  "weekpassword",
  "daypassword1",
  "hourpassword",
  "minutepasswd",
  "secondpasswd",
  "timepassword",
  "clockpasswd1",
  "watchpasswd1",
  "timerpasswd1",
  "alarmpasswd1",
  "bellpassword",
  "ringpassword",
  "soundpasswd1",
  "noisepasswd1",
  "musicpasswd1",
  "songpassword",
  "tunepassword",
  "melodypasswd",
  "harmonypass1",
  "rhythmpasswd",
  "beatpassword",
  "tempopasswd1",
  "pacepassword",
  "speedpasswd1",
  "fastpassword",
  "quickpasswd1",
  "rapidpasswd1",
  "swiftpasswd1",
  "slowpassword",
  "gentlepasswd",
  "softpassword",
  "hardpassword",
  "toughpasswd1",
  "strongpasswd",
  "weakpassword",
  "mildpassword",
  "calmpassword",
  "quietpasswd1",
  "loudpassword",
  "brightpasswd",
  "darkpassword",
  "lightpasswd1",
  "heavypasswd1",
  "thickpasswd1",
  "thinpassword",
  "widepassword",
  "narrowpasswd",
  "broadpasswd1",
  "deeppassword",
  "shallowpass1",
  "highpassword",
  "lowpassword1",
  "tallpassword",
  "shortpasswd1",
  "longpassword",
  "bigpassword1",
  "smallpasswd1",
  "largepasswd1",
  "tinypassword",
  "hugepassword",
  "giantpasswd1",
  "massivepass1",
  "enormouspa1s",
]);

/**
 * Check if a password is in the common passwords list
 * Case-insensitive check
 */
export function isCommonPassword(password: string): boolean {
  if (!password || password.length < 12) {
    return false;
  }
  return COMMON_PASSWORDS.has(password.toLowerCase());
}

/**
 * Get the size of the common passwords list
 */
export function getCommonPasswordsCount(): number {
  return COMMON_PASSWORDS.size;
}
